import type { AlignMent, ButtonItem, InputValueItem } from '@lighthouse/core'
import type { FieldInputADTValue } from '@lighthouse/core'
import type { FieldInputErrors, FormModuleType, SubFormErrors } from '@lighthouse/shared'
import { ActionButton, FormModuleProvider, getCursorStyle, isEmptyFieldInputValue, useApplicationContext } from '@lighthouse/shared'
import { reduce } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { ContainerLayout } from '../ContainerLayout'
import { getSaveFieldInputList, toErrorFocusBlockId } from './help'
import type { PageModuleFormProps } from './types'

const SCxContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`

const SCxButtonContainer = styled.div<Pick<React.CSSProperties, 'justifyContent'>>`
    padding: 0 var(--block-padding) 0 3px;
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: ${({ justifyContent }) => justifyContent};
`

const buttonAlignMap: Record<AlignMent, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end'
}

export const PageModuleForm: React.FC<PageModuleFormProps> = props => {
    const {
        children,
        blocks,
        node,
        userRecord,
        dataSource,
        record,
        block,
        style,
        dataSourceList,
        onUpdateCell,
        onRenderTitle,
        onSubmit,
        onTriggerEvent,
        getFormValues,
        formId
    } = props

    const { config, id: blockId, title } = block

    const { pointer = '', submitButton, updateType, submitType, cursor } = config ?? {}
    const { isBuilder } = useApplicationContext()

    const [loading, setLoading] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [formResetKey, setFormResetKey] = useState(0)
    const [errors, setErrors] = useImmer<FieldInputErrors>({})
    const [serviceErrors, setServiceErrors] = useImmer<FieldInputErrors>({})
    const [subFormErrors, setSubFormErrors] = useImmer<SubFormErrors>([])
    const [subFormServiceErrors, setSubFormServiceErrors] = useImmer<SubFormErrors>([])

    const isShowButton = useMemo(() => {
        return (node.children ?? []).length > 0 && !(submitType === 'UPDATE' && updateType === 'REALTIME')
    }, [node.children, submitType, updateType])

    const { schema } = dataSource ?? {}

    const handleChange = useCallback((blockId: string, v: FieldInputADTValue) => {
        // Deprecated: field block validates itself and reports errors.
        void blockId
        void v
    }, [])

    const handleBlur = useCallback((blockId: string, v: FieldInputADTValue) => {
        // Deprecated: field block validates itself and reports errors.
        void blockId
        void v
    }, [])

    const handleSmsCodeChange = useCallback((_blockId: string, _code: string) => {}, [])

    const handleSubmit = useCallback(async () => {
        if (!schema) {
            return
        }
        setSubmitAttempted(true)
        const { fieldInputList, subFormDataMap } = getFormValues()
        const saveInputList = getSaveFieldInputList(fieldInputList, userRecord)

        const isAllEmpty = reduce<InputValueItem, boolean>(
            (pre, cur) => {
                const field = schema?.[cur.fieldId]
                return Boolean(pre && isEmptyFieldInputValue(cur, field?.type))
            },
            true,
            saveInputList
        )
        const isExistFieldError = Object.values(errors).some(err => err && Object.keys(err).length > 0)
        const isExistSubFormError = subFormErrors.some(v => v.error && Object.keys(v.error).length > 0)
        const isExistServiceError = Object.values(serviceErrors).some(err => err && Object.keys(err).length > 0)
        const isExistSubFormServiceError = subFormServiceErrors.some(v => v.error && Object.keys(v.error).length > 0)

        if (isAllEmpty || isExistFieldError || isExistSubFormError || isExistServiceError || isExistSubFormServiceError) {
            const allErrors = { ...errors, ...serviceErrors }
            toErrorFocusBlockId({ inputList: saveInputList, errors: allErrors, schema })
            return
        }
        if (!loading) {
            setLoading(true)
            const res = await onSubmit?.(saveInputList, pointer, subFormDataMap)
            const { repeatFieldIds, record, subFormRepeat } = res ?? {}

            // Apply server-side repeat errors without scanning block configs.
            if ((repeatFieldIds ?? []).length > 0) {
                setServiceErrors(draft => {
                    saveInputList.forEach(item => {
                        if (repeatFieldIds?.includes(item.fieldId)) {
                            const field = schema?.[item.fieldId]
                            if (field) {
                                draft[item.id] = {
                                    ...(draft[item.id] ?? {}),
                                    repeat: { message: `${field.name}不可重复`, type: 'repeat' }
                                }
                            }
                        }
                    })
                    toErrorFocusBlockId({ inputList: saveInputList, errors: draft, schema })
                })
            }

            if (subFormRepeat && subFormRepeat.length > 0) {
                setSubFormServiceErrors(draft => {
                    // remove existing repeat errors
                    for (let i = draft.length - 1; i >= 0; i--) {
                        const e = draft[i]
                        if (e.error?.repeat) {
                            draft.splice(i, 1)
                        }
                    }
                    subFormRepeat.forEach(sr => {
                        const records = subFormDataMap[sr.subFormId] ?? []
                        sr.repeatRecord.forEach(rr => {
                            const rec = records.find(r => r.id === rr.recordId)
                            if (!rec) {
                                return
                            }
                            Object.entries(rec.content ?? {}).forEach(([columnId, cell]) => {
                                if (rr.repeatFieldIds.includes((cell as any).fieldId)) {
                                    draft.push({
                                        blockId: sr.subFormId,
                                        recordId: rr.recordId,
                                        columnId,
                                        error: { repeat: { message: '不可重复', type: 'repeat' } } as any
                                    })
                                }
                            })
                        })
                    })
                })
            }

            setLoading(false)
            const noRepeatErrors = !(repeatFieldIds ?? []).length && !subFormRepeat?.length
            const isSubmitSuccess = noRepeatErrors && !res?.failureMessage && res?.record != null
            if (isSubmitSuccess) {
                setFormResetKey(k => k + 1)
                setSubmitAttempted(false)
            }
        }
    }, [
        schema,
        getFormValues,
        userRecord,
        errors,
        subFormErrors,
        serviceErrors,
        subFormServiceErrors,
        loading,
        onSubmit,
        pointer,
        setServiceErrors,
        setSubFormServiceErrors
    ])

    const reportFieldError = useCallback(
        (blockId: string, error: any | undefined) => {
            setErrors(draft => {
                if (!error || Object.keys(error).length === 0) {
                    Reflect.deleteProperty(draft, blockId)
                    return
                }
                draft[blockId] = error
            })
        },
        [setErrors]
    )

    const resetFieldServiceError = useCallback(
        (blockId: string) => {
            setServiceErrors(draft => {
                Reflect.deleteProperty(draft, blockId)
            })
        },
        [setServiceErrors]
    )

    const updateSubFormErrors = useCallback(
        (updater: (draft: SubFormErrors) => void) => {
            setSubFormErrors(draft => {
                updater(draft)
            })
        },
        [setSubFormErrors]
    )

    const formValue: FormModuleType = useMemo(
        () => ({
            formId,
            pointer,
            errors,
            serviceErrors,
            subFormErrors,
            subFormServiceErrors,
            type: submitType === 'UPDATE' && updateType === 'REALTIME' ? 'field' : 'form',
            dataSource,
            record,
            onChange: handleChange,
            onBlur: handleBlur,
            onCodeChange: handleSmsCodeChange,
            reportFieldError,
            resetFieldServiceError,
            updateSubFormErrors,
            submitAttempted,
            formResetKey,
            onUpdateCell
        }),
        [
            formId,
            pointer,
            errors,
            serviceErrors,
            subFormErrors,
            subFormServiceErrors,
            submitType,
            updateType,
            dataSource,
            record,
            handleChange,
            handleBlur,
            handleSmsCodeChange,
            reportFieldError,
            resetFieldServiceError,
            updateSubFormErrors,
            submitAttempted,
            formResetKey,
            onUpdateCell
        ]
    )

    const buttonConfig: Omit<ButtonItem, 'id'> = useMemo(
        () => ({
            eventList: [],
            ...submitButton
        }),
        [submitButton]
    )

    const { appId = '' } = useApplicationContext()

    const cursorValue = useMemo(() => {
        const isModify = cursor?.enable && cursor?.value === 'auto'
        if (isModify) {
            return 'auto'
        }

        return getCursorStyle(appId, cursor).cursor
    }, [appId, cursor])

    return (
        <FormModuleProvider value={formValue}>
            <SCxContainer onSubmit={handleSubmit} style={style}>
                <ContainerLayout
                    blockName={title}
                    node={node}
                    disabled={!isBuilder}
                    style={{ width: '100%', height: '100%', cursor: cursorValue }}
                >
                    {children}
                    {node.type === 'formContainer' && isShowButton && (
                        <SCxButtonContainer justifyContent={buttonAlignMap[submitButton.align]}>
                            <ActionButton
                                radius={submitButton.radius}
                                size={submitButton.size}
                                config={buttonConfig}
                                loading={loading}
                                // isSubmit
                                onClick={handleSubmit}
                                data-stop-action-propagation
                                onRenderTitle={onRenderTitle}
                                onTriggerEvent={onTriggerEvent}
                            />
                        </SCxButtonContainer>
                    )}
                </ContainerLayout>
            </SCxContainer>
        </FormModuleProvider>
    )
}
